import type React from "react";
import { createContext, useContext, useReducer, useEffect } from "react";
import { sessionUtils, setStore } from "@/lib/session-utils";
import { store } from "@/src/store/store.ts";
import { resetScanState } from "@/src/store/scanSlice";
import { resetDSPMScanState } from "@/src/store/dspmScanSlice";
import { resetLeakGuardState } from "@/src/store/leakGuardSlice";
import { resetFullVulnerabilityScanState } from "@/src/store/VS_Slice";
import { resetCSPMScanState } from "@/src/store/cspmScanSlice";
import { setUser, setToken,setLoginAt, setRefreshToken, setApiKey, setRoles, setModules, setPermissions, setTenantId, resetUserState } from "@/src/store/userSlice";

// Set the store instance in session-utils
setStore(store);

export interface User {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  provider: "email" | "google" | "apple";
  license?: {
    scan_limit?: number;
    location?: string;
    issued_at?: string;
    valid_until?: string;
    is_active?: boolean;
  };
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
}

interface AuthContextType {
  state: AuthState;
  login: (user: User) => void;
  logout: () => void;
}

type AuthAction =
  | { type: "LOGIN"; payload: User }
  | { type: "LOGOUT" }
  | { type: "SET_LOADING"; payload: boolean };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        isLoading: false,
      };
    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        isLoading: false,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = () => {
      try {
        // Since we're not storing user in sessionStorage, check if Redux has user
        const userState = store.getState().user;
        if (userState.isAuthenticated && userState.user) {
          dispatch({ type: "LOGIN", payload: userState.user });
        }
      } catch (error) {
        console.error("Error checking auth:", error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    checkAuth();
  }, []);

    const login = (user: User) => {
    try {
      const now = Date.now();
      
      // FIX: Use store.dispatch for Redux actions
      store.dispatch(setUser(user));
      store.dispatch(setLoginAt(now)); // Changed from dispatch() to store.dispatch()

      // Keep dispatch() for local Context updates
      dispatch({ type: "LOGIN", payload: user });
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };


  const logout = () => {
    try {
      // Clear all session data
      sessionUtils.clearSession();

      // Clear remember me data from localStorage
      // localStorage.removeItem("remember_me");
      // localStorage.removeItem("saved_username");

      // Reset Redux state
      store.dispatch(resetScanState());
      store.dispatch(resetDSPMScanState());
      store.dispatch(resetLeakGuardState());
      store.dispatch(resetFullVulnerabilityScanState());
      store.dispatch(resetCSPMScanState());
      store.dispatch(resetUserState());

      dispatch({ type: "LOGOUT" });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
